import { customFetch } from '@/lib/api/fetcher'
import {
  appsDeficitsApiGetProjectDeficits,
  appsWorkordersApiGetWorkOrder,
  appsWorkordersApiListCutPlanLines,
  appsWorkordersApiListIssueLines,
  appsWorkordersApiListOutputLines,
  appsWorkordersApiListTaskLines,
  getAppsDeficitsApiGetProjectDeficitsQueryKey,
  getAppsWorkordersApiGetWorkOrderQueryKey,
  getAppsWorkordersApiListCutPlanLinesQueryKey,
  getAppsWorkordersApiListIssueLinesQueryKey,
  getAppsWorkordersApiListOutputLinesQueryKey,
  getAppsWorkordersApiListTaskLinesQueryKey,
} from '@/lib/api/generated/endpoints'
import type { CutPlanLineOut } from '@/lib/api/generated/types.ts/cutPlanLineOut'
import type { CutPlanPlacementOut } from '@/lib/api/generated/types.ts/cutPlanPlacementOut'
import type { DeficitsResponse } from '@/lib/api/generated/types.ts/deficitsResponse'
import type { IssueLineOut } from '@/lib/api/generated/types.ts/issueLineOut'
import type { OutputLineOut } from '@/lib/api/generated/types.ts/outputLineOut'
import type { TaskLineOut } from '@/lib/api/generated/types.ts/taskLineOut'
import type { WorkOrderOut } from '@/lib/api/generated/types.ts/workOrderOut'
import { queryOptions } from '@tanstack/react-query'

/**
 * Query factories for the WO Detail page (W4-B13).
 *
 * The orval-generated `customFetch` returns the raw body; the typed
 * envelope shapes are an artifact of the generator. We `as unknown` cast
 * to the model interfaces — same pattern the rest of the WO feature uses
 * (`work-orders-list-query.ts`).
 *
 * `staleTime: 15s` matches the WO list cadence — the detail page lives
 * close to mutations (issue/post/cancel + line edits), so a tight
 * window keeps the UI honest while still amortising tab-focus refetches.
 */

const STALE = 15_000

export const workOrderQuery = (workOrderId: number) =>
  queryOptions({
    queryKey: getAppsWorkordersApiGetWorkOrderQueryKey(workOrderId),
    queryFn: async ({ signal }) =>
      (await appsWorkordersApiGetWorkOrder(workOrderId, { signal })) as unknown as WorkOrderOut,
    staleTime: STALE,
  })

export const issueLinesQuery = (workOrderId: number) =>
  queryOptions({
    queryKey: getAppsWorkordersApiListIssueLinesQueryKey(workOrderId),
    queryFn: async ({ signal }) =>
      (await appsWorkordersApiListIssueLines(workOrderId, {
        signal,
      })) as unknown as readonly IssueLineOut[],
    staleTime: STALE,
  })

export const outputLinesQuery = (workOrderId: number) =>
  queryOptions({
    queryKey: getAppsWorkordersApiListOutputLinesQueryKey(workOrderId),
    queryFn: async ({ signal }) =>
      (await appsWorkordersApiListOutputLines(workOrderId, {
        signal,
      })) as unknown as readonly OutputLineOut[],
    staleTime: STALE,
  })

export const taskLinesQuery = (workOrderId: number) =>
  queryOptions({
    queryKey: getAppsWorkordersApiListTaskLinesQueryKey(workOrderId),
    queryFn: async ({ signal }) =>
      (await appsWorkordersApiListTaskLines(workOrderId, {
        signal,
      })) as unknown as readonly TaskLineOut[],
    staleTime: STALE,
  })

export const cutPlanLinesQuery = (workOrderId: number) =>
  queryOptions({
    queryKey: getAppsWorkordersApiListCutPlanLinesQueryKey(workOrderId),
    queryFn: async ({ signal }) =>
      (await appsWorkordersApiListCutPlanLines(workOrderId, {
        signal,
      })) as unknown as readonly CutPlanLineOut[],
    staleTime: STALE,
  })

/**
 * All placements of a WO's cut plan in one request — a plan of hundreds of
 * bars used to fire one request per bar before the page could render.
 * The key sits under `/api/work-orders/{id}/`, so the WO-detail and
 * production-chain invalidations refresh it with the plan.
 */
export const getWorkOrderCutPlanPlacementsQueryKey = (workOrderId: number) =>
  [`/api/work-orders/${workOrderId}/cut-plan-placements`] as const

export const workOrderCutPlanPlacementsQuery = (workOrderId: number) =>
  queryOptions({
    queryKey: getWorkOrderCutPlanPlacementsQueryKey(workOrderId),
    queryFn: async ({ signal }) =>
      (await customFetch(getWorkOrderCutPlanPlacementsQueryKey(workOrderId)[0], {
        signal,
        method: 'GET',
      })) as unknown as readonly CutPlanPlacementOut[],
    staleTime: STALE,
  })

/**
 * Project-scoped deficits feed — used by output-lines-table to render the
 * «Кількість якої не вистачає» column. Live per-Part `required - on_hand`.
 * Wider staleTime than the WO-local queries because deficits are derived
 * from BOM + stock, not user-driven mutations on the detail page.
 */
export const projectDeficitsQuery = (projectId: number) =>
  queryOptions({
    queryKey: getAppsDeficitsApiGetProjectDeficitsQueryKey(projectId),
    queryFn: async ({ signal }) =>
      (await appsDeficitsApiGetProjectDeficits(projectId, {
        signal,
      })) as unknown as DeficitsResponse,
    staleTime: 30_000,
  })
